import { nanoid } from '@reduxjs/toolkit';

import type { FlatStringObject } from '../commonTypes';
import type { ContentPageResponse } from '../store/models/contentPageAPI';

export const getContent = (data: ContentPageResponse, lang: string, brand: string) => {
  if (!data.length) {
    return '';
  }

  if (data.length === 1) {
    return data[0].content;
  }

  const parsedData: FlatStringObject = data.reduce(
    (acc, current) => ({
      ...acc,
      [current.language.language_id]: current.content,
    }),
    {},
  );

  const langWithBrand = brand ? [lang, brand].join('-') : lang;

  return parsedData[langWithBrand] || parsedData[lang] || parsedData.en;
};

export const formContent = (content: string, userId: string) => {
  if (!content?.length) {
    return '';
  }

  const replaceRegExp = /src="\/uploads\//gim;
  const replaceUserId = /\[user_account_id\]/g;

  const transformedContent = content.replace(replaceRegExp, 'src="/uploads/');

  if (userId) {
    return transformedContent.replace(replaceUserId, userId);
  }

  return transformedContent;
};

const normalizeStyleText = (input: string): string => {
  const parts = input
    .split(';')
    .map((str) => str.trim())
    .filter(Boolean)
    .map((str) => (str.endsWith(';') ? str.slice(0, -1) : str));

  const ruleList: string[] = [];

  for (const str of parts) {
    const i = str.indexOf(':');

    if (i === -1) continue;

    const prop = str.slice(0, i).trim().toLowerCase();
    let rule = str.slice(i + 1).trim();

    rule = rule.replace(/\s*!important\s*$/i, ' !important');
    ruleList.push(`${prop}:${rule}`);
  }
  ruleList.sort();
  return ruleList.join(';');
};

export const deinlineStyles = (
  rawHtml: string,
  nonce: string,
  opts?: {
    classPrefix?: string;
  },
): string => {
  const { classPrefix = 'c' } = opts ?? {};
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');
  const existingStyles = doc.querySelectorAll('style');

  existingStyles.forEach((s) => {
    if (nonce) {
      s.setAttribute('nonce', nonce);
    }
  });

  const nodeList = doc.querySelectorAll<HTMLElement | SVGElement>('[style]');
  const styleMap = new Map<string, string>();
  const rules: Array<{ className: string; css: string }> = [];

  nodeList.forEach((el) => {
    const raw = el.getAttribute('style') || '';
    const norm = normalizeStyleText(raw);

    if (!norm) {
      el.removeAttribute('style');
      return;
    }

    let className = styleMap.get(norm);

    if (!className) {
      className = `${classPrefix}${nanoid()}`;
      styleMap.set(norm, className);
      rules.push({
        className,
        css: norm,
      });
    }

    const prevClass = el.getAttribute('class');

    el.setAttribute('class', prevClass ? `${prevClass} ${className}` : className);
    el.removeAttribute('style');
  });

  if (rules.length > 0) {
    if (!doc.head) {
      const head = doc.createElement('head');
      doc.documentElement.insertBefore(head, doc.body || null);
    }

    if (rules.length > 0) {
      const styleEl = doc.createElement('style');
      if (nonce) styleEl.setAttribute('nonce', nonce);
      styleEl.setAttribute('data-generated', 'deinline');
      styleEl.textContent = rules.map((r) => `.${r.className}{${r.css}}`).join('\n');
      doc.head.appendChild(styleEl);
    }
  }

  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
};
